/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  Asset,
  AssetOption,
  AssetOptionType,
  AssetUtils,
  DestructuredDuration,
  DurationUtils,
  FileFormat,
  OptionUtils,
  Percentage,
  PpvModule,
  Price,
  ProductPrice,
  ProductPriceStatus,
  PurchaseOption,
  PurchaseOptionType,
  Stream,
  Vod,
} from '@wit/vtv-x-foundation-lib';
import Constants from 'configs/Constants';
import translate from 'services/i18n/translate';
import PricesUtils from '../../../../../services/utils/pricesUtils';

function getOptionTitle(isRental: boolean, format: FileFormat, price: Price): string {
  let formatLabel;
  switch (format) {
    case FileFormat.MAIN_UHD:
      formatLabel = isRental ? translate['buttons.rental.uhd'] : translate['buttons.purchase.uhd'];
      break;
    case FileFormat.MAIN_HD:
      formatLabel = isRental ? translate['buttons.rental.hd'] : translate['buttons.purchase.hd'];
      break;
    default:
      formatLabel = isRental ? translate['buttons.rental.sd'] : translate['buttons.purchase.sd'];
      break;
  }

  const priceLabel = PricesUtils.formatPrice(price);
  const fromLabel = translate.formatString(translate.modalPriceLabel, { priceLabel }).toString();
  return `${formatLabel} ${fromLabel}`;
}

function checkIsRental(fullLifeCycle: DestructuredDuration): boolean {
  const rentalDestructuredDuration = DurationUtils.toDestructuredDuration(Constants.RENTED_ENTITLEMENT_DURATION);
  if (rentalDestructuredDuration) {
    const maxRentalDuration = DurationUtils.destructuredDurationtoMilliseconds(rentalDestructuredDuration);
    const fullLifeCycleDuration = DurationUtils.destructuredDurationtoMilliseconds(fullLifeCycle);
    return maxRentalDuration > fullLifeCycleDuration;
  }
  return false;
}

function buildPurchaseOption(
  ppvModule: PpvModule,
  asset: Vod,
  stream: Stream,
  filePrice: ProductPrice,
  vat: Percentage
): PurchaseOption {
  const isRental = checkIsRental(ppvModule.fullLifeCycle);

  const finalPrice = { ...filePrice.price, amount: OptionUtils.calcFinalPrice(filePrice.price.amount, vat) };

  return {
    key: `${AssetOptionType.PURCHASE}-${isRental ? PurchaseOptionType.RENT : PurchaseOptionType.BUY}-${asset.id}-${
      stream.id
    }`,
    type: AssetOptionType.PURCHASE,
    purchaseOptionType: isRental ? PurchaseOptionType.RENT : PurchaseOptionType.BUY,
    format: stream.fileFormat,
    title: getOptionTitle(isRental, stream.fileFormat, finalPrice),
    productPrice: filePrice,
    asset,
    ppvModule,
  };
}

function aggregateOptions(options: PurchaseOption[]): AssetOption[] | undefined {
  if (options.length === 0) {
    return undefined;
  }

  const rentOptions: PurchaseOption[] = [];
  const purchaseOptions: PurchaseOption[] = [];

  options.forEach((e) => {
    if (e.purchaseOptionType === PurchaseOptionType.RENT) {
      rentOptions.push(e);
    } else {
      purchaseOptions.push(e);
    }
  });

  const finalOptions: AssetOption[] = [];
  if (rentOptions.length) {
    finalOptions.push({
      key: `${AssetOptionType.PURCHASE}-${PurchaseOptionType.RENT}`,
      type: AssetOptionType.PURCHASE,
      title: translate['buttons.rental'],
      children: rentOptions,
      multiOptions: true,
    });
  }

  if (purchaseOptions.length) {
    finalOptions.push({
      key: `${AssetOptionType.PURCHASE}-${PurchaseOptionType.BUY}`,
      type: AssetOptionType.PURCHASE,
      title: translate['buttons.purchase'],
      children: purchaseOptions,
      multiOptions: true,
    });
  }

  return finalOptions;
}

function getOptions(
  clones: Asset[],
  vodsFilesPrices: ProductPrice[],
  representativePpvModules: PpvModule[],
  vat: Percentage
): AssetOption[] {
  const options: AssetOption[] = [];

  const allowedFilesPrices = vodsFilesPrices.filter(({ status }) => status === ProductPriceStatus.NOT_PURCHASED);

  if (allowedFilesPrices.length === 0) {
    return options;
  }

  const vodAssets = clones.filter((e) => AssetUtils.isVod(e)) as Vod[];

  vodAssets.forEach((vodAsset) => {
    const purchaseOptions: PurchaseOption[] = [];
    vodAsset.streams.forEach((stream) => {
      // To avoid duplications caused by sd/hd/npvr file types using the same file, if the stream id was already added don't add it twice
      const alreadyAdded = purchaseOptions.find((option) => option.productPrice.fileId === stream.id);
      if (alreadyAdded) return;

      const filePrice = allowedFilesPrices.find((price) => price.fileId === stream.id);

      if (filePrice) {
        const ppvModule = representativePpvModules.find((e) => e.id === filePrice.ppvModuleId);
        if (ppvModule) {
          purchaseOptions.push(buildPurchaseOption(ppvModule, vodAsset, stream, filePrice, vat));
        }
      }
    });
    const finalOptions = aggregateOptions(purchaseOptions);
    if (finalOptions !== undefined) {
      options.push(...finalOptions);
    }
  });

  return options;
}

export default {
  getOptions,
};
